import React from "react";
import { observer } from "mobx-react-lite";
import { IconChevronDown, IconFilter, IconPlus } from "@tabler/icons-react";
import clsx from "clsx";
import FilterMenu from "./FilterMenu";
import { ColumnSuggestionsOUT, PropertyBrowserTreeNode } from "api-client";
import DropdownBrowser from "components/DropdownBrowser";
import { useTranslation } from "react-i18next";
import useOutsideClick from "hooks/useOutsideClick";
import { EMPTY_FILTER_RULE } from "features/DynamicTable/consts";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";

export interface FilterRule {
  property: PropertyBrowserTreeNode | undefined;
  operator: string;
  value: string;
  suggestedValues?: ColumnSuggestionsOUT[];
}

export default observer(function FilterByPropertyBrowser(props: {
  advancedMode?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";

  const openIconRef = React.useRef<HTMLDivElement>(null);
  const closeView = () => dynamicTableStore.filterStore.setOpenFilter(false);

  useOutsideClick(openIconRef, closeView);

  const handleOpenMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    hasAnyPropertyRule &&
      dynamicTableStore.filterStore.setOpenFilter(
        !dynamicTableStore.filterStore.openFilter
      );
  };

  const onChangeSuggestedValues = (
    values: ColumnSuggestionsOUT[],
    index: number
  ) => {
    const rules = [
      ...dynamicTableStore.filterStore.selectedFilterByItems.rules,
    ];
    rules[index].suggestedValues = values;
    dynamicTableStore.filterStore.updateFilterRules(rules);
  };

  const onChangeTerm = (value: string | number | undefined, index: number) => {
    const rules = [
      ...dynamicTableStore.filterStore.selectedFilterByItems.rules,
    ];
    rules[index].value = String(value);
    dynamicTableStore.filterStore.updateFilterRules(rules);
  };

  function clearTerm(index: number) {
    onChangeSuggestedValues([], index);
    onChangeTerm("", index);
  }

  const addFirstRule = (value: PropertyBrowserTreeNode[]) => {
    dynamicTableStore.filterStore.updateFilterRules([EMPTY_FILTER_RULE]);
    onChangeProperty(value, 0, true);
  };

  const onChangeProperty = async (
    value: PropertyBrowserTreeNode[],
    index: number,
    openMenu?: boolean
  ) => {
    clearTerm(index);
    const rules = [
      ...dynamicTableStore.filterStore.selectedFilterByItems.rules,
    ];
    rules[index].property = value[0];
    dynamicTableStore.filterStore.updateFilterRules(rules);
    openMenu && dynamicTableStore.filterStore.setOpenFilter(true);
    const suggestedValues = await dynamicTableStore.fetchColumnSuggestions(
      value[0]
    );
    suggestedValues && onChangeSuggestedValues(suggestedValues, index);
  };

  const clientRect = openIconRef.current?.getBoundingClientRect();

  const hasAnyPropertyRule =
    dynamicTableStore.filterStore.selectedFilterByItems.rules
      .map((item) => item.property)
      .filter(Boolean).length > 0;

  function labelContent() {
    return (
      <div className="flex items-center">
        <IconFilter width={17} className="mr-1" stroke={3} />
        <span className="mr-1">
          {dynamicTableStore.filterStore.validFilterRules?.rules.length
            ? `${t("mappingEditor.filtered")} ${t("mappingEditor.by")}: `
            : t("mappingEditor.filter")}
        </span>
        {dynamicTableStore.filterStore.selectedFilterByItems?.rules
          .map((rec) => {
            if (rec.property && rec.value)
              return isEng ? rec.property?.name : rec.property?.name_de;
          })
          .filter((rec) => rec)
          .join(", ")}
      </div>
    );
  }

  if (hasAnyPropertyRule) {
    return (
      <div
        className="flex items-center"
        ref={openIconRef}
        onClick={handleOpenMenu}
      >
        {!props.advancedMode && labelContent()}
        <div className="overflow-visible">
          <IconChevronDown
            className={clsx(
              "h-5 w-5 cursor-pointer",
              dynamicTableStore.filterStore.openFilter ? "rotate-180" : ""
            )}
          />
          {dynamicTableStore.filterStore.openFilter ? (
            <FilterMenu
              cursorPosition={{
                x: Number(clientRect?.x),
                y: Number(clientRect?.y),
              }}
              items={dynamicTableStore.properties}
              onChangeProperty={onChangeProperty}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center"
      ref={openIconRef}
      onClick={handleOpenMenu}
    >
      {hasAnyPropertyRule && !props.advancedMode && labelContent()}
      <div className="overflow-visible">
        {hasAnyPropertyRule ? (
          <>
            <IconChevronDown
              className={clsx(
                "h-5 w-5 cursor-pointer",
                dynamicTableStore.filterStore.openFilter ? "rotate-180" : ""
              )}
            />
            {dynamicTableStore.filterStore.openFilter ? (
              <FilterMenu
                cursorPosition={{
                  x: Number(clientRect?.x),
                  y: Number(clientRect?.y),
                }}
                items={dynamicTableStore.properties}
                onChangeProperty={onChangeProperty}
              />
            ) : null}
          </>
        ) : (
          <DropdownBrowser<PropertyBrowserTreeNode>
            displayKey={isEng ? "name" : "name_de"}
            detailKey={isEng ? "classification" : "classification_de"}
            handleSelect={(value) => addFirstRule(value)}
            name="filter_by"
            items={dynamicTableStore.properties}
            suggestedItems={dynamicTableStore.visibleColumnProperties}
            placeholder={t("mappingEditor.findProperty")}
            optionsClassName="!max-w-fit min-w-[246px]"
            containerClassName="relative"
            singleSelect
            fixedPos
            selectInputClassName="max-w-[134px] w-[134px]"
            titleOrIcon={
              <>
                {!props.advancedMode && labelContent()}
                <IconPlus className="h-4 w-4 cursor-pointer" />
              </>
            }
            isOpen={(open: boolean) =>
              dynamicTableStore.filterStore.setOpenFilter(open)
            }
          />
        )}
      </div>
    </div>
  );
});
